import { View, StyleSheet } from "react-native";
import {
  useGetUserLearningPreferencesQuery,
  useUpdateUserLearningPreferencesMutation,
} from "@axio/graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import AccordionShell from "../../../../components/common/general/CustomAccordion/AccordionShell";
import SettingsAccordionItem from "../../../../components/common/general/CustomAccordion/SettingsAccordionItem";
import { Sparkles, BookOpen, GraduationCap, Target, Bell } from "lucide-react";
import { useCompanionPreferencesForm } from "../../../../hooks/useCompanionPreferencesForm";
import { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import InterestsEditor from "../../../../components/companion/preferences/editors/InterestsEditor";
import CareerInterestsEditor from "../../../../components/companion/preferences/editors/CareerInterestsEditor";
import ContinuingEducationEditor from "../../../../components/companion/preferences/editors/ContinuingEducationEditor";
import LearningPreferencesEditor from "../../../../components/companion/preferences/editors/LearningPreferencesEditor";
import NotificationRedirectPanel from "../../../../components/companion/preferences/editors/NotificationRedirectPanel";
import { useWatch } from "react-hook-form";
import MotivationEditor from "../../../../components/companion/preferences/editors/MotivationsEditor";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useTranslation } from "react-i18next";

const FIRST_OPEN_KEY = "ai_companion_settings_first_open_v2";

type Section = "academic" | "continuing" | "interests" | "learning" | "motivation" | "reminders";

export const AICompanionSettings = () => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<Set<Section>>(() => new Set(["academic"]));
  const lastSentRef = useRef<string | null>(null);
  const isHydratingRef = useRef(true);

  const { data, loading } = useGetUserLearningPreferencesQuery();
  const [updatePreferences] = useUpdateUserLearningPreferencesMutation();

  const methods = useCompanionPreferencesForm();
  const { watch, setValue, formState } = methods;

  const prefs = data?.getUserLearningPreferences;

  const skillsToImprove = useWatch({ control: methods.control, name: "skillsToImprove" });
  const learningStyles = useWatch({ control: methods.control, name: "learningStyles" });
  const motivations = useWatch({ control: methods.control, name: "motivations" });
  const semesterGoal = useWatch({ control: methods.control, name: "semesterGoal" });

  const values = useWatch({ control: methods.control });

  const formValues = useWatch({ control: methods.control }) ?? {};

  useEffect(() => {
    if (loading) return;

    isHydratingRef.current = true;

    methods.reset({
      interests: prefs?.interests ?? [],
      careerInterests: prefs?.careerInterests ?? [],
      continuingEducation: prefs?.continuingEducation ?? [],
      skillsToImprove: prefs?.skillsToImprove ?? [],
      learningStyles: prefs?.learningStyles ?? [],
      motivations: prefs?.motivations ?? [],
      semesterGoal: prefs?.semesterGoal ?? "",
      assignmentReminders: prefs?.assignmentReminders ?? true,
      studyTimePreference: prefs?.studyTimePreference ?? null,
      checkInFrequency: prefs?.checkInFrequency ?? null,
      textReminders: prefs?.textReminders ?? false,
    });

    // allow next tick to finish RHF internal state update
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
  }, [prefs, loading, methods]);

  useEffect(() => {
    (async () => {
      const hasOpened = await AsyncStorage.getItem(FIRST_OPEN_KEY);
      if (!hasOpened) {
        setOpenSections(new Set(["academic"]));
        await AsyncStorage.setItem(FIRST_OPEN_KEY, "1");
      }
    })().catch(console.error);
  }, []);
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (!formState.isDirty) return;
    if (!values) return;

    const serialized = JSON.stringify(values);
    if (serialized === lastSentRef.current) return;

    lastSentRef.current = serialized;

    updatePreferences({
      variables: { input: values },
    }).catch(console.error);
  }, [values, formState.isDirty, updatePreferences]);

  const toggleSection = (section: Section) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const getSelectedLabel = (count: number) => {
    return t("text.companion_preferences_selected_count", { count });
  };

  if (loading) {
    return <Skeleton height={200} />;
  }

  return (
    <View style={styles.container}>
      <FormProvider {...methods}>
        <AccordionShell>
          <SettingsAccordionItem
            index={0}
            icon={<BookOpen size={18} />}
            title={t("text.companion_preferences_academic_interests")}
            rightLabel={getSelectedLabel(formValues?.careerInterests?.length ?? 0)}
            expanded={openSections.has("academic")}
            onToggle={() => toggleSection("academic")}>
            <CareerInterestsEditor
              value={watch("careerInterests")}
              onChange={(v) => setValue("careerInterests", v, { shouldDirty: true })}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={1}
            icon={<GraduationCap size={18} />}
            title={t("text.companion_preferences_continuing_education")}
            rightLabel={getSelectedLabel(formValues?.continuingEducation?.length ?? 0)}
            expanded={openSections.has("continuing")}
            onToggle={() => toggleSection("continuing")}>
            <ContinuingEducationEditor
              value={watch("continuingEducation")}
              onChange={(v) => setValue("continuingEducation", v, { shouldDirty: true })}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={2}
            icon={<Sparkles size={18} />}
            title={t("text.companion_preferences_personal_interests")}
            rightLabel={getSelectedLabel(formValues?.interests?.length ?? 0)}
            expanded={openSections.has("interests")}
            onToggle={() => toggleSection("interests")}>
            <InterestsEditor
              value={watch("interests")}
              onChange={(v) => setValue("interests", v, { shouldDirty: true })}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={3}
            icon={<BookOpen size={18} />}
            title={t("text.companion_preferences_how_you_learn")}
            rightLabel={getSelectedLabel((skillsToImprove?.length ?? 0) + (learningStyles?.length ?? 0))}
            expanded={openSections.has("learning")}
            onToggle={() => toggleSection("learning")}>
            <LearningPreferencesEditor
              value={{
                skillsToImprove: skillsToImprove ?? [],
                learningStyles: learningStyles ?? [],
              }}
              onChange={(v) => {
                setValue("skillsToImprove", v.skillsToImprove, { shouldDirty: true });
                setValue("learningStyles", v.learningStyles, { shouldDirty: true });
              }}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={4}
            icon={<Target size={18} />}
            title={t("text.companion_preferences_motivation")}
            rightLabel={getSelectedLabel(motivations?.length ?? 0)}
            expanded={openSections.has("motivation")}
            onToggle={() => toggleSection("motivation")}>
            <MotivationEditor
              value={{
                motivations: motivations ?? [],
                semesterGoal: semesterGoal ?? "",
              }}
              onChange={(v) => {
                setValue("motivations", v.motivations, { shouldDirty: true });
                setValue("semesterGoal", v.semesterGoal, { shouldDirty: true });
              }}
            />
          </SettingsAccordionItem>

          <SettingsAccordionItem
            index={5}
            icon={<Bell size={18} />}
            title={t("text.companion_preferences_stay_on_track")}
            rightLabel={
              formValues?.assignmentReminders
                ? t("text.companion_preferences_toggle_on")
                : t("text.companion_preferences_toggle_off")
            }
            expanded={openSections.has("reminders")}
            onToggle={() => toggleSection("reminders")}
            isLast>
            <NotificationRedirectPanel />
          </SettingsAccordionItem>
        </AccordionShell>
      </FormProvider>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    gap: 24,
  },
});
